/**
 * AFOTOT-a · los aforos y el albergue, juntos y sumados (pieza pura, sin E/S).
 *
 * Selecciona los elementos donde cabe gente, los marca, detecta posibles repetidos y
 * prepara las columnas y los totales que usan las hojas XLSX/ODS y el CSV aparte.
 *
 * Criterio sellado por Luis (30-sep-2026) y DOP de `AFOTOT` («a, a, a, b, a, a, a»):
 * - Entran los elementos con aforo declarado, con ficha de albergue o aptos; los aptos
 *   sin cifra entran marcados «sin cifra» (2 b).
 * - El total principal suma solo los aptos; aparte, los no aptos o sin clasificar con
 *   cifra que el plan propone (3 c).
 * - Aforo declarado y plazas de albergue nunca se suman entre sí.
 * - Posible repetido (DOP-2 a): mismo documento, nombre parecido (Jaccard ≥ 0,6 sobre
 *   palabras significativas), misma tipología y a ≤ 150 m, o alguno sin ubicar. Se avisa
 *   y se cuenta una vez (4 b); si las dos filas traen cifras distintas, cuentan las dos
 *   con aviso de revisar (DOP-3 b).
 * - Orden (DOP-4 a): aforo de mayor a menor, luego plazas, luego nombre; «sin cifra» al final.
 */
import type { ProcessedRecord } from '@/types/export.types';
import { TIPOLOGIAS } from '@/constants/export.constants';

export type UbicacionAforo = 'ubicada' | 'centroide' | 'sin_coordenadas';

const ROTULO_UBICACION: Record<UbicacionAforo, string> = {
  ubicada: 'Ubicada',
  centroide: 'Centroide del municipio',
  sin_coordenadas: 'Sin coordenadas',
};

const ROTULO_LIMITE: Record<string, string> = {
  superficie: 'superficie', aseos: 'aseos', duchas: 'duchas',
};

/** Unidades del aforo que se suman como personas. */
const UNIDADES_PERSONAS = new Set(['personas', 'persona', 'plazas']);

/** Distancia máxima, en metros, para dar dos filas por el mismo edificio. */
export const DISTANCIA_REPETIDO_M = 150;
/** Parecido mínimo de nombres (Jaccard de palabras significativas). */
export const PARECIDO_REPETIDO = 0.6;

/** Una fila de la hoja «Aforos y albergue». */
export interface FilaAforo {
  record: ProcessedRecord;
  apto: boolean;
  aforo?: number;
  unidad?: string;
  plazas?: number;
  limite?: string;
  /**
   * ALBRTA-b2c-2 · plazas del Registro de Turismo de Andalucía de la fila (`registroRTA`).
   * Columna y totales propios: nunca se suman con el aforo declarado ni con la capacidad DGPC,
   * y no cuentan para «sin cifra», que se refiere a las cifras del plan (DOP-5 a).
   */
  plazasRegistro?: number;
  /**
   * ALBRTA-b2c-3a (DOP-1/2 a, Luis, 2-oct-2026): fila del plan sin aforo, ficha ni aptitud que
   * entra solo por las plazas del registro. Marca «solo registro», fuera de los totales y de
   * «Elementos…»; se cuenta en sus dos totales propios.
   */
  soloRegistro: boolean;
  sinCifra: boolean;
  ubicacion: UbicacionAforo;
  /** Nombres de las otras filas con las que puede ser el mismo edificio. */
  repetidoDe: string[];
  /** `false` si su cifra ya la aporta otra fila del mismo edificio. */
  cuentaCifra: boolean;
  /** `false` si otra fila del mismo edificio ya cuenta (o trae cifra). */
  cuentaSinCifra: boolean;
  avisos: string[];
}

const VACIAS = new Set('de del la las el los y a en al d e c p s n ntra sra'.split(' '));
const SINONIMOS: Record<string, string> = {
  ceip: 'colegio', cp: 'colegio', ies: 'instituto', pab: 'pabellon',
  polideprotivo: 'polideportivo', ayto: 'ayuntamiento', mpal: 'municipal',
};

/**
 * Palabras significativas del nombre, como en la medida de `AFOTOT`: sin tildes, sin el
 * bloque «AFORO…», sin letras sueltas (las siglas con puntos, «C.E.I.P.», se caen) y con
 * CEIP / C.P. / Colegio Público unificados en «colegio».
 */
export function palabrasNombre(nombre: string): Set<string> {
  const base = (nombre || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/aforo.*$/, '')
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\bcolegio publico\b/g, 'colegio');
  const out = new Set<string>();
  for (const w of base.split(/\s+/)) {
    const p = SINONIMOS[w] ?? w;
    if (p.length > 1 && !VACIAS.has(p)) out.add(p);
  }
  return out;
}

export function parecidoNombres(a: string, b: string): number {
  const A = palabrasNombre(a), B = palabrasNombre(b);
  if (!A.size || !B.size) return 0;
  let comunes = 0;
  for (const w of A) if (B.has(w)) comunes++;
  return comunes / (A.size + B.size - comunes);
}

export type TipoCentro = 'colegio' | 'instituto';

/**
 * AFOTOT-a2 · DOP-3 a″: tipo de centro educativo leído del nombre original, con las siglas
 * compactadas antes de quitar los puntos («C.E.I.P.», «I.E.S.», «C.P.R»), porque
 * `palabrasNombre` borra las letras sueltas y con ellas la sigla. `null` si no es ninguno o
 * si el nombre nombra los dos.
 */
export function tipoCentro(nombre: string): TipoCentro | null {
  const base = (nombre || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/aforo.*$/, '')
    .replace(/\b([a-z](?:\s?\.\s?[a-z]){1,4})\b\.?/g, (_, s: string) => ` ${s.replace(/[\s.]/g, '')} `)
    .replace(/[^a-z0-9 ]/g, ' ');
  const w = new Set(base.split(/\s+/));
  const colegio = ['colegio', 'ceip', 'cp', 'cpr'].some((p) => w.has(p));
  const instituto = ['instituto', 'ies'].some((p) => w.has(p));
  if (colegio === instituto) return null;
  return colegio ? 'colegio' : 'instituto';
}

function ubicacionDe(r: ProcessedRecord): UbicacionAforo {
  if (r.procedencia === 'centroide') return 'centroide';
  if (!r.geocodificado || !r.xUtm30 || !r.yUtm30) return 'sin_coordenadas';
  return 'ubicada';
}

/** ¿Entra en la hoja? Aforo declarado, ficha de albergue o apto (criterio 2 b). */
export function entraEnAforos(r: ProcessedRecord): boolean {
  return r.aforoDeclarado != null || r.fichaAlbergue != null || r.albergueEstado === 'Apto';
}

/**
 * ALBRTA-b2c-3a (DOP-4 a de `b2c-2`): fila que no entra por el plan pero trae plazas del
 * Registro de Turismo de Andalucía. Entra marcada «solo registro» y se cuenta aparte.
 */
export function entraSoloPorRegistro(r: ProcessedRecord): boolean {
  return !entraEnAforos(r) && r.registroRTA?.plazasRegistro != null;
}

function conCifra(f: FilaAforo): boolean {
  return !f.sinCifra;
}

function mismoEdificio(a: FilaAforo, b: FilaAforo): boolean {
  if (a.record.tipologia !== b.record.tipologia) return false;
  // DOP-3 a″ (Luis, 30-sep): un colegio y un instituto nunca son el mismo edificio.
  const ca = tipoCentro(a.record.nombre), cb = tipoCentro(b.record.nombre);
  if (ca && cb && ca !== cb) return false;
  if (parecidoNombres(a.record.nombre, b.record.nombre) < PARECIDO_REPETIDO) return false;
  if (a.ubicacion !== 'ubicada' || b.ubicacion !== 'ubicada') return true;
  const d = Math.hypot(a.record.xUtm30 - b.record.xUtm30, a.record.yUtm30 - b.record.yUtm30);
  return d <= DISTANCIA_REPETIDO_M;
}

const cmpNombre = (a: FilaAforo, b: FilaAforo) => a.record.nombre.localeCompare(b.record.nombre, 'es');

/** DOP-4 a: aforo ↓, plazas ↓, nombre; los «sin cifra» al final, por nombre. */
function comparar(a: FilaAforo, b: FilaAforo): number {
  // ALBRTA-b2c-3a: las «solo registro», detrás de todas las filas del plan.
  if (a.soloRegistro !== b.soloRegistro) return a.soloRegistro ? 1 : -1;
  if (a.sinCifra !== b.sinCifra) return a.sinCifra ? 1 : -1;
  const fa = a.aforo ?? -1, fb = b.aforo ?? -1;
  if (fa !== fb) return fb - fa;
  const pa = a.plazas ?? -1, pb = b.plazas ?? -1;
  if (pa !== pb) return pb - pa;
  return cmpNombre(a, b);
}

const firma = (f: FilaAforo) => `${f.aforo ?? ''}|${f.plazas ?? ''}`;

/**
 * Filas de la hoja, ya marcadas y ordenadas. Se asume un solo documento (la exportación
 * de P4 es de un plan): los repetidos se buscan entre todas las filas recibidas.
 */
export function seleccionarAforos(registros: ProcessedRecord[]): FilaAforo[] {
  const filas: FilaAforo[] = registros.filter((r) => entraEnAforos(r) || entraSoloPorRegistro(r)).map((r) => {
    const cap = r.capacidadAlbergue;
    const plazas = cap?.calculable ? cap.plazas : undefined;
    const aforo = r.aforoDeclarado ?? undefined;
    return {
      record: r,
      apto: r.albergueEstado === 'Apto',
      aforo,
      unidad: aforo != null ? (r.aforoUnidad || 'personas') : undefined,
      plazas,
      limite: plazas !== undefined && cap?.limite ? ROTULO_LIMITE[cap.limite] : undefined,
      plazasRegistro: r.registroRTA?.plazasRegistro ?? undefined,
      soloRegistro: !entraEnAforos(r),
      sinCifra: aforo == null && plazas === undefined,
      ubicacion: ubicacionDe(r),
      repetidoDe: [],
      cuentaCifra: true,
      cuentaSinCifra: true,
      avisos: [...(cap?.avisos ?? [])],
    };
  });
  filas.sort(comparar);

  // Grupos de posibles repetidos (componentes conexas de la relación «mismo edificio»).
  const grupo = filas.map((_, i) => i);
  const raiz = (i: number): number => (grupo[i] === i ? i : (grupo[i] = raiz(grupo[i])));
  // ALBRTA-b2c-3a: las filas «solo registro» no entran en la búsqueda de repetidos, para que
  // no muevan qué fila del plan cuenta (ni «Aptos sin cifra»).
  for (let i = 0; i < filas.length; i++) {
    if (filas[i].soloRegistro) continue;
    for (let j = i + 1; j < filas.length; j++) {
      if (filas[j].soloRegistro) continue;
      if (mismoEdificio(filas[i], filas[j])) {
        filas[i].repetidoDe.push(filas[j].record.nombre);
        filas[j].repetidoDe.push(filas[i].record.nombre);
        grupo[raiz(j)] = raiz(i);
      }
    }
  }
  const grupos = new Map<number, FilaAforo[]>();
  filas.forEach((f, i) => {
    if (!f.repetidoDe.length) return;
    const g = grupos.get(raiz(i)) ?? [];
    g.push(f);
    grupos.set(raiz(i), g);
  });

  for (const g of grupos.values()) {
    const conCifras = g.filter(conCifra);
    // Contar una vez (4 b): cifras idénticas cuentan una vez; distintas, todas (DOP-3 b).
    const vistas = new Set<string>();
    for (const f of conCifras) {
      if (vistas.has(firma(f))) f.cuentaCifra = false;
      vistas.add(firma(f));
    }
    const distintas = vistas.size > 1;
    // Las filas sin cifra de un edificio que ya aparece solo cuentan si ninguna trae cifra, y una vez.
    g.filter((f) => f.sinCifra).forEach((f, k) => {
      f.cuentaSinCifra = conCifras.length === 0 && k === 0;
    });
    for (const f of g) {
      const otras = [...new Set(f.repetidoDe)].map((n) => `«${n}»`).join(', ');
      if (distintas && conCifra(f)) {
        f.avisos.push(`posible repetido de ${otras} con cifras distintas: cuentan las dos, revisar`);
      } else if ((f.sinCifra && !f.cuentaSinCifra) || (!f.sinCifra && !f.cuentaCifra)) {
        f.avisos.push(`posible repetido de ${otras}: no se suma otra vez`);
      } else {
        f.avisos.push(`posible repetido de ${otras}: se suma esta fila`);
      }
    }
  }

  for (const f of filas) {
    if (f.aforo != null && f.unidad && !UNIDADES_PERSONAS.has(f.unidad.toLowerCase())) {
      f.avisos.push(`aforo en ${f.unidad}: no se suma como personas`);
    }
  }
  return filas;
}

// ───────────────────────────── Columnas y totales ─────────────────────────────

export interface ColumnaAforo {
  /** Rótulo legible (XLSX, ODS, informe HTML). */
  rotulo: string;
  /** Código corto (CSV y formatos SIG; ≤ 10 caracteres para el DBF). */
  codigo: string;
  ancho: number;
}

/** ALBRTA-b2c-2 · rótulo de la columna del registro (ARRANQUE §1). */
export const ROTULO_PLAZAS_REGISTRO = 'Plazas del Registro de Turismo de Andalucía publicado (OpenRTA)';
/** ALBRTA-b2c-2 (DOP-5 a): «sin cifra» es del plan; el registro va aparte y no se suma. */
export const NOTA_REGISTRO_AFOROS =
  '«Sin cifra» se refiere a las cifras del plan; las plazas del Registro de Turismo de Andalucía van en su columna, aparte, y no se suman con ninguna otra cifra.';
/** ALBRTA-b2c-3a · marca y total de las filas que entran solo por el registro. */
export const MARCA_SOLO_REGISTRO = 'solo registro';
export const ROTULO_SOLO_REGISTRO_FILAS = 'Filas del plan solo con plazas del registro (sin aforo, ficha ni aptitud)';

/** DOP-1 a: columnas visibles, en este orden. */
export const COLUMNAS_AFOROS: ColumnaAforo[] = [
  { rotulo: 'Nombre', codigo: 'NOMBRE', ancho: 40 },
  { rotulo: 'Tipología', codigo: 'TIPOLOGIA', ancho: 14 },
  { rotulo: 'Dirección', codigo: 'DIRECCION', ancho: 34 },
  { rotulo: 'Aptitud', codigo: 'APTITUD', ancho: 13 },
  { rotulo: 'Aforo declarado', codigo: 'AFORO', ancho: 11 },
  { rotulo: 'Unidad', codigo: 'AFORO_UD', ancho: 10 },
  { rotulo: 'Plazas de albergue', codigo: 'ALB_PLAZAS', ancho: 11 },
  { rotulo: 'Límite', codigo: 'ALB_LIMITE', ancho: 11 },
  // ALBRTA-b2c-2 (DOP-1 a): columna propia del registro, junto a las cifras del plan y aparte.
  { rotulo: ROTULO_PLAZAS_REGISTRO, codigo: 'RTA_PLAZAS', ancho: 16 },
  { rotulo: 'Sin cifra', codigo: 'SIN_CIFRA', ancho: 9 },
  { rotulo: 'Posible repetido', codigo: 'REPETIDO', ancho: 30 },
  { rotulo: 'Avisos', codigo: 'AVISOS', ancho: 50 },
  { rotulo: 'X UTM30', codigo: 'X_UTM30', ancho: 12 },
  { rotulo: 'Y UTM30', codigo: 'Y_UTM30', ancho: 12 },
  { rotulo: 'Ubicación', codigo: 'UBICACION', ancho: 18 },
];

/**
 * Columnas auxiliares ocultas: cada una aporta a un total, para que `SUBTOTAL` sume solo
 * lo visible tras filtrar y, a la vez, separe aptos de no aptos. No van al CSV.
 */
export const TOTALES_AFOROS = [
  { clave: 'aforoAptos', rotulo: 'Aforo declarado de aptos (personas)' },
  { clave: 'plazasAptos', rotulo: 'Plazas de albergue de aptos' },
  { clave: 'fichasCeroAptos', rotulo: 'Fichas de aptos que dan 0 plazas' },
  { clave: 'aptosSinCifra', rotulo: 'Aptos sin cifra' },
  { clave: 'noAptosConCifra', rotulo: 'Más no aptos o sin clasificar que el plan propone' },
  { clave: 'aforoNoAptos', rotulo: 'Su aforo declarado (personas)' },
  { clave: 'plazasNoAptos', rotulo: 'Sus plazas de albergue' },
  // ALBRTA-b2c-2 (DOP-2 a): al final, para no mover ningún total anterior; aptos y, aparte,
  // no aptos o sin clasificar (criterio 3 c). Solo plazas del registro: no se suman con nada.
  { clave: 'registroAptos', rotulo: 'Plazas del Registro de Turismo de Andalucía (OpenRTA) de aptos' },
  { clave: 'registroNoAptos', rotulo: 'Plazas del Registro de Turismo de Andalucía (OpenRTA) de no aptos o sin clasificar' },
  // ALBRTA-b2c-3a (DOP-2 a): al final, para no mover nada; filas que entran solo por el registro.
  { clave: 'soloRegistroFilas', rotulo: ROTULO_SOLO_REGISTRO_FILAS },
  { clave: 'soloRegistroPlazas', rotulo: 'Sus plazas del Registro de Turismo de Andalucía (OpenRTA)' },
] as const;

export type ClaveTotalAforos = (typeof TOTALES_AFOROS)[number]['clave'];
export type TotalesAforos = Record<ClaveTotalAforos, number>;

const sumaPersonas = (f: FilaAforo) =>
  f.aforo != null && f.cuentaCifra && UNIDADES_PERSONAS.has((f.unidad ?? 'personas').toLowerCase());

/** Aporte de una fila a cada total (valor de sus columnas auxiliares; `null` = celda vacía). */
export function aporteFila(f: FilaAforo): Record<ClaveTotalAforos, number | null> {
  // ALBRTA-b2c-3a (DOP-2 a): una fila «solo registro» solo aporta a sus dos totales propios.
  if (f.soloRegistro) {
    const vacio = Object.fromEntries(TOTALES_AFOROS.map((c) => [c.clave, null])) as Record<ClaveTotalAforos, number | null>;
    return { ...vacio, soloRegistroFilas: 1, soloRegistroPlazas: f.plazasRegistro ?? null };
  }
  const plazas = f.plazas !== undefined && f.cuentaCifra ? f.plazas : null;
  return {
    aforoAptos: f.apto && sumaPersonas(f) ? (f.aforo as number) : null,
    plazasAptos: f.apto ? plazas : null,
    fichasCeroAptos: f.apto && plazas === 0 ? 1 : null,
    aptosSinCifra: f.apto && f.sinCifra && f.cuentaSinCifra ? 1 : null,
    noAptosConCifra: !f.apto && !f.sinCifra && f.cuentaCifra ? 1 : null,
    aforoNoAptos: !f.apto && sumaPersonas(f) ? (f.aforo as number) : null,
    plazasNoAptos: !f.apto ? plazas : null,
    // Cada inscripción del registro casa con una sola fila: no hay doble cuenta entre filas.
    registroAptos: f.apto ? f.plazasRegistro ?? null : null,
    registroNoAptos: !f.apto ? f.plazasRegistro ?? null : null,
    soloRegistroFilas: null,
    soloRegistroPlazas: null,
  };
}

/** Totales sin filtro (informe HTML y pruebas); en las hojas los calcula `SUBTOTAL`. */
export function totalesAforos(filas: FilaAforo[]): TotalesAforos {
  const t = Object.fromEntries(TOTALES_AFOROS.map((c) => [c.clave, 0])) as TotalesAforos;
  for (const f of filas) {
    const a = aporteFila(f);
    for (const c of TOTALES_AFOROS) t[c.clave] += a[c.clave] ?? 0;
  }
  return t;
}

// ─────────────── AFOTOT-a2 · bloque de totales para «Resumen» e informes ───────────────

export const TITULO_RESUMEN_AFOROS = 'AFOROS Y ALBERGUE';
/** DOP-2 a: cifras estáticas, sin filtro, rotuladas igual que la pestaña. */
export const NOTA_RESUMEN_AFOROS =
  'Cifras de todo el plan, sin filtro: el detalle y los filtros están en la pestaña «Aforos y albergue». ' +
  'Aforo declarado y plazas de albergue no se suman entre sí. ' + NOTA_REGISTRO_AFOROS;
export const SIN_AFOROS_RESUMEN =
  'Ningún elemento del plan trae aforo declarado, ficha de albergue ni aptitud para albergue.';
export const ROTULO_ELEMENTOS_AFOROS = 'Elementos con aforo, ficha de albergue o aptos';
export const ROTULO_EN_CENTROIDE = 'De ellos, en el centroide del municipio (sin ubicar)';
export const ROTULO_SIN_COORDENADAS = 'De ellos, sin coordenadas';

/**
 * Filas «rótulo · cifra» del bloque de totales: cuántos entran, los totales de la
 * pestaña (misma cuenta, `totalesAforos`) y cuántos quedan sin ubicar. `[]` si no entra nada.
 */
export function filasResumenAforos(filas: FilaAforo[]): Array<[string, number]> {
  if (!filas.length) return [];
  const t = totalesAforos(filas);
  // ALBRTA-b2c-3a: «Elementos…» y los «De ellos…» son de las filas del plan; las «solo
  // registro» se cuentan en su total propio (DOP-2 a).
  const delPlan = filas.filter((f) => !f.soloRegistro);
  const cuenta = (u: UbicacionAforo) => delPlan.filter((f) => f.ubicacion === u).length;
  return [
    [ROTULO_ELEMENTOS_AFOROS, delPlan.length],
    ...TOTALES_AFOROS.map((c): [string, number] => [c.rotulo, t[c.clave]]),
    [ROTULO_EN_CENTROIDE, cuenta('centroide')],
    [ROTULO_SIN_COORDENADAS, cuenta('sin_coordenadas')],
  ];
}

// ─────────────── AFOTOT-a3 · capa aparte en los formatos SIG ───────────────

/** Nombre de la capa (fichero GeoJSON, capa SHP) y rótulo de la carpeta KML. */
export const CAPA_AFOROS = 'aforos_albergue';
export const TITULO_CAPA_AFOROS = 'Aforos y albergue';

/** DOP-3 a: en el mapa solo van las filas ubicadas; las demás se cuentan en los totales. */
export function filasCapaAforos(filas: FilaAforo[]): FilaAforo[] {
  return filas.filter((f) => f.ubicacion === 'ubicada');
}

/**
 * Nota de la capa: los totales son de todo el plan y cuántos elementos no salen en el mapa.
 * Mismas palabras que el bloque de totales; `''` si no entra nada.
 */
export function notaCapaAforos(filas: FilaAforo[]): string {
  if (!filas.length) return '';
  // ALBRTA-b2c-3a (DOP-3 a): las «solo registro» salen en la capa con su marca, pero la cuenta
  // «X de N elementos» sigue siendo la de las filas del plan; se nombran aparte.
  const delPlan = filas.filter((f) => !f.soloRegistro);
  const soloReg = filas.filter((f) => f.soloRegistro);
  const enMapa = filasCapaAforos(delPlan).length;
  const centroide = delPlan.filter((f) => f.ubicacion === 'centroide').length;
  const sinCoord = delPlan.filter((f) => f.ubicacion === 'sin_coordenadas').length;
  const fuera: string[] = [];
  if (centroide) fuera.push(`${centroide} en el centroide del municipio`);
  if (sinCoord) fuera.push(`${sinCoord} sin coordenadas`);
  let extra = '';
  if (soloReg.length) {
    const enMapaReg = filasCapaAforos(soloReg).length;
    const n = soloReg.length;
    extra = ` Además, ${n === 1 ? 'entra 1 fila' : `entran ${n} filas`} del plan solo con plazas del registro` +
      ` (marca «${MARCA_SOLO_REGISTRO}»), ${enMapaReg === n ? (n === 1 ? 'que sale' : 'que salen') : `de las que salen ${enMapaReg}`} en el mapa.`;
  }
  return (
    `Totales de todo el plan, sin filtro. En el mapa salen ${enMapa} de ${delPlan.length} elementos` +
    (fuera.length ? `; no salen ${fuera.join(' y ')}.` : '.') +
    extra +
    ' Aforo declarado y plazas de albergue no se suman entre sí.'
  );
}

/** Propiedades de una fila de la capa: códigos de `COLUMNAS_AFOROS` → valores visibles. */
export function propiedadesFilaAforo(f: FilaAforo): Record<string, string | number | null> {
  const v = valoresFila(f);
  return Object.fromEntries(COLUMNAS_AFOROS.map((c, i) => [c.codigo, v[i]]));
}

/** Valores visibles de una fila, alineados con `COLUMNAS_AFOROS` (`null` = celda vacía). */
export function valoresFila(f: FilaAforo): Array<string | number | null> {
  const r = f.record;
  const ubicada = f.ubicacion === 'ubicada';
  return [
    r.nombre,
    TIPOLOGIAS[r.tipologia]?.label ?? r.tipologia,
    r.direccion || null,
    r.albergueEstado || 'Sin clasificar',
    f.aforo ?? null,
    f.unidad ?? null,
    f.plazas ?? null,
    f.limite ?? null,
    f.plazasRegistro ?? null,
    // ALBRTA-b2c-3a (DOP-1 a): misma columna; «solo registro» manda sobre «sin cifra».
    f.soloRegistro ? MARCA_SOLO_REGISTRO : f.sinCifra ? 'sin cifra' : null,
    f.repetidoDe.length ? [...new Set(f.repetidoDe)].join(' · ') : null,
    f.avisos.length ? f.avisos.join(' · ') : null,
    ubicada ? Math.round(r.xUtm30 * 100) / 100 : null,
    ubicada ? Math.round(r.yUtm30 * 100) / 100 : null,
    ROTULO_UBICACION[f.ubicacion],
  ];
}

// Disposición de la hoja: título, rótulos de totales, totales, nota, cabecera, datos.
export const HOJA_AFOROS = 'Aforos y albergue';
export const FILA_TITULO = 1;
export const FILA_ROTULOS_TOTALES = 2;
export const FILA_TOTALES = 3;
export const FILA_NOTA = 4;
export const FILA_CABECERA = 5;
export const NOTA_AFOROS =
  'Los totales suman solo las filas visibles: si filtras, cambian. Aforo declarado y plazas de albergue no se suman entre sí. ' +
  NOTA_REGISTRO_AFOROS;

/** Letra de columna (1 → A, 27 → AA). */
export function letraColumna(n: number): string {
  let s = '';
  for (let k = n; k > 0; k = Math.floor((k - 1) / 26)) s = String.fromCharCode(65 + ((k - 1) % 26)) + s;
  return s;
}

/** Fórmula `SUBTOTAL(109, …)` del total `i` (0-based) para `n` filas de datos. */
export function formulaTotal(i: number, n: number): string {
  const col = letraColumna(COLUMNAS_AFOROS.length + 1 + i);
  const desde = FILA_CABECERA + 1;
  const hasta = FILA_CABECERA + Math.max(n, 1);
  return `SUBTOTAL(109,${col}${desde}:${col}${hasta})`;
}

/** Contenido del CSV aparte: cabecera de códigos + filas visibles, sin totales (DOP-6 a). */
export function csvAforos(filas: FilaAforo[], sep = ';', eol = '\r\n'): string {
  const esc = (v: string | number | null): string => {
    if (v == null) return '';
    const s = typeof v === 'number' ? String(v) : v;
    return /[";\r\n]/.test(s) || s.includes(sep) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lineas = [COLUMNAS_AFOROS.map((c) => c.codigo).join(sep)];
  for (const f of filas) lineas.push(valoresFila(f).map(esc).join(sep));
  return lineas.join(eol);
}
